import { useEffect } from 'react';
import { useAppContext } from '../AppContext';
import { lookupPrank, selectRandomPrank } from './index';
import { Intensity } from './types';

const APRIL = 3;
const RANDOM_KNARP = 'rand';
const ANY_KNARP = 'sudorand';
const INTENSE_PREFIX = 'ntns';
const NORMAL_PREFIX = 'nrml';

const isAprilFirst = (): boolean => {
  const today = new Date();
  return today.getMonth() === APRIL && today.getDate() === 1;
};

// `react_component_with_context` is invoked multiple times in the layout, so
// AprilFoolsPrank ends up mounted in several React roots on the same page.
// This module-level lock ensures only one instance actually fires the prank.
let activeInstanceId: number | null = null;
let nextInstanceId = 1;

const AprilFoolsPrank = () => {
  const { currentUser } = useAppContext();
  const isInternalAdmin = currentUser?.isHaldaAdmin ?? false;

  useEffect(() => {
    if (!isInternalAdmin) return;

    const params = new URLSearchParams(window.location.search);
    const knarp = params.get('knarp');
    const isTestMode = knarp !== null;

    let result;
    if (isTestMode) {
      let stripped = knarp;
      let forcedIntensity: typeof Intensity[keyof typeof Intensity] | null = null;
      if (knarp.startsWith(INTENSE_PREFIX)) {
        stripped = knarp.slice(INTENSE_PREFIX.length);
        forcedIntensity = Intensity.INTENSE;
      } else if (knarp.startsWith(NORMAL_PREFIX)) {
        stripped = knarp.slice(NORMAL_PREFIX.length);
        forcedIntensity = Intensity.NORMAL;
      }
      const isRandomTest = stripped === RANDOM_KNARP || stripped === ANY_KNARP;
      const includeDisabled = stripped === ANY_KNARP;

      if (isRandomTest) {
        result = selectRandomPrank(
          includeDisabled ? [] : (currentUser?.uiAnimDisabledPranks ?? []),
        );
        if (result && forcedIntensity) {
          result = { ...result, intensity: forcedIntensity };
        }
      } else {
        result = lookupPrank(knarp);
      }
    } else if (isAprilFirst() && !currentUser?.uiAnimExempt) {
      result = selectRandomPrank(currentUser?.uiAnimDisabledPranks ?? []);
    } else if (currentUser?.targetedPrankCode) {
      result = lookupPrank(currentUser.targetedPrankCode);
    }
    if (!result) return;

    if (activeInstanceId !== null) return;
    const instanceId = nextInstanceId;
    nextInstanceId += 1;
    activeInstanceId = instanceId;

    const cleanup = result.prank.create(result.intensity);

    return () => {
      cleanup?.();
      if (activeInstanceId === instanceId) {
        activeInstanceId = null;
      }
    };
  }, [isInternalAdmin, currentUser?.uiAnimExempt, currentUser?.uiAnimDisabledPranks, currentUser?.targetedPrankCode]);

  return null;
};

export default AprilFoolsPrank;
